import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Observable, of } from 'rxjs';
import { isNotNil } from '@web/utils';
import {
  ImageThumbnailComponent,
  ImageThumbnailUnsupportedComponent,
  NoDataComponent,
  TableBodyNoReferenceCellComponent,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { BoxComponentGroupCosmeticView } from '../../models';

@Component({
  selector: 'app-box-component-group-cosmetics-table',
  imports: [
    ImageThumbnailComponent,
    ImageThumbnailUnsupportedComponent,
    NgOptimizedImage,
    NoDataComponent,
    TableBodyNoReferenceCellComponent,
    TableComponent,
  ],
  templateUrl: './box-component-group-cosmetics-table.component.html',
  styleUrl: './box-component-group-cosmetics-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableService],
})
export class BoxComponentGroupCosmeticsTableComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<BoxComponentGroupCosmeticView, never>).create();
  private readonly table = inject(TableService);

  public readonly items = input.required<BoxComponentGroupCosmeticView[]>();
  public readonly showAvailableQuantity = input(true);
  public readonly showRemainingQuantity = input(true);

  protected config!: TableConfig<BoxComponentGroupCosmeticView, never>;
  protected readonly isNotNil = isNotNil;
  /* prettier-ignore */
  protected readonly getColumn = <Key extends keyof BoxComponentGroupCosmeticView>(key: Key): Key => key;

  public constructor() {
    effect(() => {
      this.items();
      this.refreshListAfterItemsChanges();
    });
  }

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .pagination(false)
      .filtering(false)
      .columns((builder) => {
        const columns = [
          builder
            .initCustom('featureFileUrl')
            .width('6rem')
            .label('Zdjęcie ')
            .build(),

          builder.initCustom('name').width('15rem').label('Nazwa').build(),

          builder
            .initCustom('cosmeticBrandName')
            .width('12rem')
            .label('Marka')
            .build(),

          builder
            .initCustom('amount')
            .width('8rem')
            .label('Ilość w pudełku')
            .build(),
        ];

        if (this.showAvailableQuantity()) {
          columns.push(
            builder
              .initCustom('availableQuantity')
              .width('14rem')
              .label('Dostępna ilość do kupienia')
              .build(),
          );
        }

        if (this.showRemainingQuantity()) {
          columns.push(
            builder
              .initNumber('remainingQuantity')
              .width('14rem')
              .label('Pozostała ilość do kupienia')
              .build(),
          );
        }

        return columns;
      })
      .dataSource({
        fetch: (): Observable<
          TableDataPayload<BoxComponentGroupCosmeticView>
        > => {
          const items = this.items();

          return of(new TableDataPayload(items, items.length, 1, items.length));
        },
      })
      .build();
  }

  private refreshListAfterItemsChanges(): void {
    this.table.refresh();
  }
}
